import PropTypes from "prop-types";
import styled from "styled-components";
import { useField } from "formik";
import { ReactNode, useEffect, useRef } from "react";
import { toMultiSelectStringArray } from "utils/GuidedSignUp/toMultiSelectStringArray";

import type { CheckboxTags } from "components/GuidedSignUp/GuidedSignUpCheckbox";
import { StepLayoutClassName } from "components/Step/StepLayout";
import { FOOTER_HEIGHT } from "components/Step/StepFooter";

const CheckboxContainer = styled.div`
  position: relative;
`;

/** Outer pill: border/background wrap the full control + expanded content */
/** Durations aligned with ExpandableShell so close feels one continuous motion */
const PILL_TRANSITION_MS = "0.38s";

const OtherPill = styled.div<{
  $checked: boolean;
  $checkedBorderColor: string;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  transition: background-color ${PILL_TRANSITION_MS} ease-in-out,
    border-color ${PILL_TRANSITION_MS} ease-in-out,
    padding ${PILL_TRANSITION_MS} ease-in-out,
    color ${PILL_TRANSITION_MS} ease-in-out;
  background-color: ${({ $checked, theme }) =>
    $checked ? "#B3C9FF4D" : theme.colors["white"]};
  border: 1px solid;
  border-color: ${({ $checked, $checkedBorderColor, theme }) =>
    $checked ? $checkedBorderColor : theme.colors["newsprint3"]};
  border-radius: 28px;
  padding: ${({ $checked }) => ($checked ? "1rem" : "0.875rem")};
  color: ${({ $checked, theme }) =>
    $checked ? theme.colors["perriwinkle-dark"] : theme.colors["ink-black"]};
`;

/** Top row: only this region toggles selection via native label */
const OtherTopRow = styled.label`
  position: relative;
  display: grid;
  grid-template-columns: 1.5rem 1fr 1.5rem;
  place-items: center;
  grid-gap: 1rem;
  line-height: 1.375;
  width: 100%;
  margin: 0;
  cursor: pointer;
`;

/**
 * Height transition: grid row 0fr → 1fr (inner min-height:0 + overflow hidden).
 * Keeps expanded content mounted so opening never flashes empty.
 */
const ExpandableShell = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? "1fr" : "0fr")};
  margin-top: ${({ $open }) => ($open ? "0.75rem" : "0")};
  transition: grid-template-rows ${PILL_TRANSITION_MS} ease-in-out,
    margin-top ${PILL_TRANSITION_MS} ease-in-out;
`;

const ExpandableClip = styled.div`
  min-height: 0;
  overflow: hidden;
`;

const NonToggleBody = styled.div<{ $open: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  pointer-events: ${({ $open }) => ($open ? "auto" : "none")};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transform: ${({ $open }) => ($open ? "translateY(0)" : "translateY(-8px)")};
  /* Opening: wait for shell to start expanding; closing: fade/move immediately so content isn’t visibly “squished” */
  transition: opacity 0.22s ease-out ${({ $open }) => ($open ? "0.12s" : "0ms")},
    transform 0.32s cubic-bezier(0.33, 1, 0.68, 1)
      ${({ $open }) => ($open ? "0.12s" : "0ms")};
`;

const CheckboxInput = styled.input`
  position: absolute;
  opacity: 0;
`;

const CheckboxControl = styled.span<{
  $checked: boolean;
  $suppressCheckmarkIcon: boolean;
}>`
  position: relative;
  background-color: ${({ $checked, $suppressCheckmarkIcon, theme }) =>
    $checked && !$suppressCheckmarkIcon
      ? theme.colors["perriwinkle"]
      : "transparent"};
  border-radius: 50%;
  display: flex;
  width: 1.5rem;
  height: 1.5rem;
  transition: opacity 125ms ease-in-out;
  opacity: ${({ $checked }) => ($checked ? "1" : "0")};
`;

const CheckboxIcon = styled.svg`
  margin: auto;
`;

const VisualLabel = styled.span`
  text-align: center;
`;

const Tags = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: absolute;
  inset: 0;
  background: transparent;
  border-radius: 28px;
  overflow: hidden;
`;

const Tag = styled.div`
  padding: 0.25rem 0.5rem;
  border-radius: 3px;
  background: #546ac9;
  color: white;
  font-size: 0.625rem;
  font-weight: 900;
`;

const OtherTextarea = styled.textarea`
  display: block;
  width: 100%;
  height: 5.5rem;
  min-height: 5.5rem;
  max-height: 5.5rem;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors["newsprint3"]};
  font-family: inherit;
  font-size: 1rem;
  line-height: 1.375;
  resize: none;
  box-sizing: border-box;
  background: ${({ theme }) => theme.colors["white"]};
  color: ${({ theme }) => theme.colors["ink-black"]};

  &::placeholder {
    color: ${({ theme }) => theme.colors["warmGray1"]};
  }

  &:focus {
    outline: none;
    box-shadow: none;
  }
`;

const OtherIdeasSubheading = styled.p`
  margin: 0;
  font-size: 0.875rem;
  font-weight: 800;
  line-height: 1.35;
  color: ${({ theme }) => theme.colors["perriwinkle-dark"]};
`;

const OtherIdeasList = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
  list-style-type: disc;
  list-style-position: outside;
  font-size: 0.875rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors["perriwinkle-dark"]};
`;

const OTHER_GOAL_PLACEHOLDER = "Write your message";

/** Match ExpandableShell grid transition (~0.38s) so height includes textarea + list before measure. */
const PILL_EXPAND_TRANSITION_MS = 380;
const OTHER_EXPAND_SCROLL_DELAY_MS = PILL_EXPAND_TRANSITION_MS + 40;

/** Space to leave between the bottom of the Other pill and the scroll region bottom (above step footer). */
const OTHER_PILL_BOTTOM_GAP_REM = 2;

function remToPx(rem: number): number {
  if (typeof window === "undefined") {
    return rem * 16;
  }
  const fs = parseFloat(getComputedStyle(document.documentElement).fontSize);
  return rem * (Number.isFinite(fs) ? fs : 16);
}

function getScrollableParent(el: HTMLElement): HTMLElement | null {
  const stepContent = el.closest(
    `.${StepLayoutClassName.CONTENT_SPACING}`
  ) as HTMLElement | null;
  if (stepContent) {
    return stepContent;
  }

  let parent = el.parentElement;
  while (parent && parent !== document.documentElement) {
    const { overflowY } = getComputedStyle(parent);
    const canScroll =
      (overflowY === "auto" || overflowY === "scroll") &&
      parent.scrollHeight > parent.clientHeight + 1;
    if (canScroll) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
}

/**
 * Scroll the full Other pill (textarea + bullet list) into view with ~gap above the sticky footer.
 * Uses StepLayout’s scrollable content region when present; otherwise window minus footer height.
 */
function scrollOtherPillWithFooterGap(pillEl: HTMLElement): void {
  if (typeof window === "undefined") {
    return;
  }

  requestAnimationFrame(() => {
    const gapPx = remToPx(OTHER_PILL_BOTTOM_GAP_REM);
    const pillRect = pillEl.getBoundingClientRect();
    const scrollEl = getScrollableParent(pillEl);

    if (scrollEl) {
      const containerRect = scrollEl.getBoundingClientRect();
      const spaceBelowPill = containerRect.bottom - pillRect.bottom;
      const shortfall = gapPx - spaceBelowPill;
      if (shortfall > 1) {
        scrollEl.scrollTo({
          top: scrollEl.scrollTop + shortfall,
          behavior: "smooth",
        });
      }
      return;
    }

    const viewportBottom = window.innerHeight - FOOTER_HEIGHT;
    const spaceBelowPill = viewportBottom - pillRect.bottom;
    const shortfall = gapPx - spaceBelowPill;
    if (shortfall > 1) {
      window.scrollTo({
        top: window.scrollY + shortfall,
        behavior: "smooth",
      });
    }
  });
}

const OTHER_GOAL_IDEAS = [
  "How should reading feel each day?",
  "Which skills do you hope grow?",
  "What memories should reading create",
] as const;

export interface GuidedSignUpCheckboxOtherProps {
  checkedBorderColor?: string;
  className?: string;
  id: string;
  label: ReactNode;
  name: string;
  onClick?: () => void;
  otherTextFieldName: string;
  suppressCheckmarkIcon?: boolean;
  tags?: CheckboxTags;
  value: string;
}

function GuidedSignUpCheckboxOther({
  checkedBorderColor = "transparent",
  className,
  label,
  suppressCheckmarkIcon = false,
  tags,
  name,
  value,
  id,
  onClick,
  otherTextFieldName,
}: GuidedSignUpCheckboxOtherProps) {
  const [field, , helpers] = useField<string[]>(name);
  const [textField, , textHelpers] = useField<string>(otherTextFieldName);
  const selected = toMultiSelectStringArray(field.value);
  const checked = selected.includes(value);
  const pillRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const prevCheckedRef = useRef(checked);

  useEffect(() => {
    const wasChecked = prevCheckedRef.current;
    prevCheckedRef.current = checked;

    if (!checked || wasChecked) {
      return;
    }

    const pill = pillRef.current;
    const textarea = textareaRef.current;
    if (!pill || !textarea || typeof window === "undefined") {
      return;
    }

    const timer = window.setTimeout(() => {
      scrollOtherPillWithFooterGap(pill);
      textarea.focus({ preventScroll: true });
    }, OTHER_EXPAND_SCROLL_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [checked]);

  const toggle = () => {
    const next = checked
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    helpers.setValue(next);
    onClick?.();
  };

  return (
    <CheckboxContainer className={className}>
      <CheckboxInput
        id={id}
        name={name}
        type="checkbox"
        checked={checked}
        onChange={toggle}
        onBlur={field.onBlur}
      />
      <OtherPill
        ref={pillRef}
        $checked={checked}
        $checkedBorderColor={checkedBorderColor}
        data-testid={id}
      >
        <OtherTopRow htmlFor={id}>
          {tags ? (
            <Tags>
              {tags.map((tag, idx) => (
                <Tag
                  css={`
                    background-color: ${tag?.bgColor || "transparent"};
                    color: ${tag?.color || "transparent"};
                  `}
                  key={idx}
                >
                  {tag?.label || ""}
                </Tag>
              ))}
            </Tags>
          ) : null}
          <CheckboxControl
            $checked={checked}
            $suppressCheckmarkIcon={suppressCheckmarkIcon}
          >
            {!suppressCheckmarkIcon ? (
              <CheckboxIcon
                width="12"
                height="9"
                viewBox="0 0 12 9"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11 1L4.125 7.875L1 4.75"
                  stroke="#fff"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </CheckboxIcon>
            ) : null}
          </CheckboxControl>
          <VisualLabel className="guided-sign-up-checkbox-label">
            {label}
          </VisualLabel>
        </OtherTopRow>
        <ExpandableShell $open={checked}>
          <ExpandableClip>
            <NonToggleBody
              $open={checked}
              aria-hidden={!checked}
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <OtherTextarea
                ref={textareaRef}
                aria-label={OTHER_GOAL_PLACEHOLDER}
                data-testid="guided-sign-up-other-goal-text"
                maxLength={70}
                name={otherTextFieldName}
                placeholder={OTHER_GOAL_PLACEHOLDER}
                tabIndex={checked ? 0 : -1}
                value={textField.value}
                onBlur={textField.onBlur}
                onChange={(e) => textHelpers.setValue(e.target.value)}
                onFocus={() => {
                  if (pillRef.current) {
                    scrollOtherPillWithFooterGap(pillRef.current);
                  }
                }}
                onKeyDown={(e) => e.stopPropagation()}
              />
              <OtherIdeasSubheading>
                Some ideas to get started
              </OtherIdeasSubheading>
              <OtherIdeasList>
                {OTHER_GOAL_IDEAS.map((idea) => (
                  <li key={idea}>{idea}</li>
                ))}
              </OtherIdeasList>
            </NonToggleBody>
          </ExpandableClip>
        </ExpandableShell>
      </OtherPill>
    </CheckboxContainer>
  );
}

GuidedSignUpCheckboxOther.defaultProps = {
  className: "",
};

GuidedSignUpCheckboxOther.propTypes = {
  className: PropTypes.string,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
};

export default GuidedSignUpCheckboxOther;
